// components/PropertyForm.jsx
import React, { useEffect, useRef } from 'react';
import BasicInfo from './sections/BasicInfo';
import RevenueSection from './sections/RevenueSection';
import OperatingExpensesSection from './sections/OperatingExpensesSection';
import MarketingDescriptionSection from './sections/MarketingDescriptionSection';
import BuildingPhotosSection from './sections/BuildingPhotosSection';
import OwnedFinancingSection from './sections/OwnedFinancingSection';
import { useLanguage } from '../contexts/LanguageContext';
import { isOwnedDeal, FINANCING_TYPE_EXISTING } from '../utils/dealPurpose';
import { useNavigate } from 'react-router-dom';

const PropertyForm = ({
  currentProperty,
  setCurrentProperty,
  advancedExpenses,
  setAdvancedExpenses,
  onSave,
  marketingSectionScrollTrigger = 0,
  userProfile = null,
  onOpenListingImport,
  listingImportWarnings = [],
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const marketingSectionRef = useRef(null);
  const lastMarketingScrollTrigger = useRef(-1);
  const owned = isOwnedDeal(currentProperty);

  useEffect(() => {
    if (!currentProperty.id || !marketingSectionRef.current) return;
    if (marketingSectionScrollTrigger <= 0) return;
    if (marketingSectionScrollTrigger <= lastMarketingScrollTrigger.current) return;
    lastMarketingScrollTrigger.current = marketingSectionScrollTrigger;
    requestAnimationFrame(() => {
      marketingSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [marketingSectionScrollTrigger, currentProperty.id]);

  const showBrokerSaleMandate =
    !owned &&
    userProfile?.persona === 'courtier_immo' &&
    userProfile?.modules?.purchaseOffersEnabled === true;

  const handleSaveClick = () => {
    if (
      !currentProperty.address ||
      !currentProperty.city ||
      !currentProperty.province ||
      !currentProperty.postalCode
    ) {
      alert(t('property.alert.fillAddress'));
      return;
    }
    if (owned && !(parseFloat(currentProperty.purchasePrice) > 0)) {
      if (!window.confirm(t('property.alert.ownedValueMissing'))) {
        return;
      }
    }
    onSave();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 overflow-x-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-between sm:items-center mb-6">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <h2 className="text-xl sm:text-2xl font-semibold" data-onboarding="form-header">
                {owned ? t('property.form.titleOwned') : t('property.form.title')}
              </h2>
              {owned && (
                <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-800">
                  {t('dealPurpose.badge.owned')}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
              <button
                type="button"
                data-onboarding="form-save"
                onClick={handleSaveClick}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shrink-0"
              >
                {t('save')}
              </button>
              <button
                type="button"
                data-onboarding="form-mode-toggle"
                onClick={() => setAdvancedExpenses(!advancedExpenses)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                {advancedExpenses ? t('property.form.simpleMode') : t('property.form.advancedMode')}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-2 py-2 text-gray-600 hover:text-gray-800"
              >
                ← {t('back')}
              </button>
            </div>
          </div>

          <div className="space-y-8">
            {!currentProperty.id && onOpenListingImport && !owned && (
              <div className="border border-dashed border-indigo-200 rounded-xl p-4 sm:p-5 bg-indigo-50/60">
                <h2 className="text-base font-semibold text-indigo-950 mb-1">
                  {t('listingImport.formBannerTitle')}
                </h2>
                <p className="text-sm text-indigo-900/85 leading-relaxed mb-3">
                  {t('listingImport.formBannerBody')}
                </p>
                <button
                  type="button"
                  onClick={onOpenListingImport}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                >
                  {t('listingImport.formBannerButton')}
                </button>
              </div>
            )}
            {listingImportWarnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm font-medium text-amber-950 mb-1">
                  {t('listingImport.formWarningsTitle')}
                </p>
                <ul className="list-disc pl-5 text-sm text-amber-900 space-y-1">
                  {listingImportWarnings.map((w, i) => (
                    <li key={`${i}-${String(w).slice(0, 24)}`}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            {!owned &&
              (!currentProperty.id ? (
                <div className="border border-dashed border-indigo-200 rounded-xl p-6 bg-gradient-to-br from-indigo-50/80 to-slate-50/60">
                  <h2 className="text-lg font-semibold text-indigo-950 mb-2">
                    {t('property.marketingMediaLockedTitle')}
                  </h2>
                  <p className="text-sm text-indigo-900/85 leading-relaxed">
                    {t('property.marketingMediaLockedBody')}
                  </p>
                </div>
              ) : (
                <div ref={marketingSectionRef} className="space-y-8 scroll-mt-6">
                  <BuildingPhotosSection
                    property={currentProperty}
                    onChange={setCurrentProperty}
                  />
                  <MarketingDescriptionSection
                    property={currentProperty}
                    onChange={setCurrentProperty}
                    readOnly={false}
                  />
                </div>
              ))}
            {showBrokerSaleMandate ? (
              <div className="border border-teal-100 rounded-lg p-4 bg-teal-50/40">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={!!currentProperty.brokerSaleMandate}
                    onChange={(e) =>
                      setCurrentProperty({
                        ...currentProperty,
                        brokerSaleMandate: e.target.checked,
                      })
                    }
                  />
                  <span>
                    <span className="text-sm font-medium text-gray-900 block">
                      {t('property.brokerSaleMandate')}
                    </span>
                    <span className="text-xs text-gray-600 block mt-0.5">
                      {t('property.brokerSaleMandateHint')}
                    </span>
                  </span>
                </label>
              </div>
            ) : null}
            <div data-onboarding="form-basic-info">
              <BasicInfo
                property={currentProperty}
                onChange={setCurrentProperty}
                advancedExpenses={advancedExpenses}
                isOwned={owned}
                purchasePriceLabel={
                  owned ? t('basicInfo.estimatedMarketValue') : undefined
                }
              />
            </div>
            <div data-onboarding="form-revenue">
              <RevenueSection
                revenue={currentProperty}
                onChange={setCurrentProperty}
                advancedExpenses={advancedExpenses}
              />
            </div>
            <div data-onboarding="form-expenses">
              <OperatingExpensesSection
                expenses={currentProperty}
                onChange={setCurrentProperty}
                advancedExpenses={advancedExpenses}
              />
            </div>
            {owned && (
              <div data-onboarding="form-owned-financing">
                <OwnedFinancingSection
                  financing={currentProperty}
                  onChange={(next) =>
                    setCurrentProperty({
                      ...currentProperty,
                      ...next,
                      financingType: next.financingType || FINANCING_TYPE_EXISTING,
                    })
                  }
                />
              </div>
            )}

            <div
              className={`flex ${
                currentProperty.id ? 'justify-end' : 'justify-between'
              }`}
            >
              {!currentProperty.id && (
                <button
                  onClick={() => navigate(-1)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
              )}
              <button
                type="button"
                data-onboarding="form-save"
                onClick={handleSaveClick}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyForm;
